/* eslint-disable @atlassian/relay/unused-fields */

import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { fireTrackAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences$key,
	JiraNotificationType,
} from '@atlassian/jira-relay/src/__generated__/notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences.graphql';
import type { notificationsConfigRow_personalSettingsNotificationsConfigTable_enabledDataFragment$key } from '@atlassian/jira-relay/src/__generated__/notificationsConfigRow_personalSettingsNotificationsConfigTable_enabledDataFragment.graphql';
import type {
	JiraNotificationChannelType,
	notificationsConfigRow_personalSettingsNotificationsConfigTable_preference$key,
} from '@atlassian/jira-relay/src/__generated__/notificationsConfigRow_personalSettingsNotificationsConfigTable_preference.graphql';
import { EMAIL, IN_PRODUCT } from '../../constants.tsx';
import type {
	HeadCheckboxIdsType,
	JiraNotificationGraphQLType,
	JiraNotificationPreference,
} from '../../types.tsx';
import { NotificationsConfigCheckbox } from '../notifications-config-checkbox/index.tsx';
import { CellContainer } from '../styled.tsx';

const ATTRIBUTES_FOR_SINGLE_ROW_UPDATE = {
	type: 'row',
	issueAssigned: 'unchanged',
	issueUpdated: 'unchanged',
	issueDeleted: 'unchanged',
	commentCreated: 'unchanged',
	commentEdited: 'unchanged',
	commentDeleted: 'unchanged',
	issueCreated: 'unchanged',
	issueMoved: 'unchanged',
	mentionsCombined: 'unchanged',
	worklogCombined: 'unchanged',
	miscellaneousIssueEventCombined: 'unchanged',
};

const snakeToCamelCase = (str: string) =>
	str.toLowerCase().replace(/([-_]\w)/g, (g) => g[1].toUpperCase());

export const NotificationsConfigRow = ({
	label,
	id,
	headCheckboxIds,
	preference,
	allPreferences,
	commitMutation,
	enabledDataFragment,
}: {
	label: string;
	id: string;
	headCheckboxIds: HeadCheckboxIdsType;
	preference: notificationsConfigRow_personalSettingsNotificationsConfigTable_preference$key;
	allPreferences: notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences$key;
	enabledDataFragment: notificationsConfigRow_personalSettingsNotificationsConfigTable_enabledDataFragment$key;
	commitMutation: (
		channel: JiraNotificationChannelType,
		isEnabled: boolean,
		newPreferences: {
			[key: string]: JiraNotificationPreference;
		},
		type: JiraNotificationType,
	) => void;
}) => {
	const enabledData = useFragment(
		graphql`
			fragment notificationsConfigRow_personalSettingsNotificationsConfigTable_enabledDataFragment on JiraNotificationGlobalPreference {
				options @required(action: THROW) {
					isEmailNotificationEnabled
				}
			}
		`,
		enabledDataFragment,
	);
	const data = useFragment(
		graphql`
			fragment notificationsConfigRow_personalSettingsNotificationsConfigTable_preference on JiraNotificationPreference {
				id @required(action: THROW)
				type @required(action: THROW)
				emailChannel @required(action: THROW) {
					channel
					isEnabled
					isEditable
				}
				inProductChannel {
					channel
					isEnabled
					isEditable
				}
			}
		`,
		preference,
	);

	const allData = useFragment(
		graphql`
			fragment notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences on JiraNotificationPreferences
			@argumentDefinitions(
				includeMiscellaneousCombinedType: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/include-miscellaneous-events-combined-type.relayprovider"
				}
			) {
				issueAssigned @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				issueUpdated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				issueDeleted @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				commentCreated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				commentEdited @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				commentDeleted @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				issueCreated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				issueMoved @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				mentionsCombined @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				worklogCombined @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				miscellaneousIssueEventCombined
					@required(action: THROW)
					@include(if: $includeMiscellaneousCombinedType) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
			}
		`,
		allPreferences,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const createJiraNotificationPreference = useCallback(
		(
			notificationType: JiraNotificationGraphQLType,
			isEnabled: boolean,
			channel: JiraNotificationChannelType,
		) => {
			const retVal: JiraNotificationPreference = {
				type: allData[notificationType]?.type,
				id: allData[notificationType]?.id ?? '',
				emailChannel: {
					channel: allData[notificationType]?.emailChannel.channel,
					isEnabled:
						data.type === allData[notificationType]?.type && channel === EMAIL
							? isEnabled
							: allData[notificationType]?.emailChannel.isEnabled,
					isEditable: allData[notificationType]?.emailChannel.isEditable,
				},
			};

			const inProductChannelName = allData[notificationType]?.inProductChannel;

			if (inProductChannelName) {
				retVal.inProductChannel = {
					channel: inProductChannelName.channel,
					isEnabled:
						data.type === allData[notificationType]?.type && channel === IN_PRODUCT
							? isEnabled
							: inProductChannelName.isEnabled,
					isEditable: inProductChannelName.isEditable,
				};
			}

			return retVal;
		},
		[allData, data],
	);

	const onCheckboxChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, channel: JiraNotificationChannelType) => {
			const isEnabled = event.target.checked;

			const newPreferences: {
				[key: string]: JiraNotificationPreference;
			} = {
				issueAssigned: createJiraNotificationPreference('issueAssigned', isEnabled, channel),
				issueUpdated: createJiraNotificationPreference('issueUpdated', isEnabled, channel),
				issueDeleted: createJiraNotificationPreference('issueDeleted', isEnabled, channel),
				commentCreated: createJiraNotificationPreference('commentCreated', isEnabled, channel),
				commentEdited: createJiraNotificationPreference('commentEdited', isEnabled, channel),
				commentDeleted: createJiraNotificationPreference('commentDeleted', isEnabled, channel),
				issueCreated: createJiraNotificationPreference('issueCreated', isEnabled, channel),
				issueMoved: createJiraNotificationPreference('issueMoved', isEnabled, channel),
				mentionsCombined: createJiraNotificationPreference('mentionsCombined', isEnabled, channel),
				worklogCombined: createJiraNotificationPreference('worklogCombined', isEnabled, channel),
				miscellaneousIssueEventCombined: createJiraNotificationPreference(
					'miscellaneousIssueEventCombined',
					isEnabled,
					channel,
				),
			};

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			commitMutation(channel, isEnabled, newPreferences, data.type!);

			const camelType = snakeToCamelCase(data.type);
			const trackAnalyticsAttributes = ATTRIBUTES_FOR_SINGLE_ROW_UPDATE;

			fireTrackAnalytics(createAnalyticsEvent({}), 'checkbox updated', 'notificationCheckbox', {
				channel,
				...trackAnalyticsAttributes,
				[camelType]: isEnabled ? 'checked' : 'unchecked',
			});
			fireUIAnalytics(createAnalyticsEvent({}), 'checkbox clicked', camelType, {
				channel,
				value: isEnabled ? 'checked' : 'unchecked',
			});
		},
		[commitMutation, createAnalyticsEvent, data, createJiraNotificationPreference],
	);

	const emailCheckboxId = `email-${id}`;
	const inProductCheckboxId = `inProduct-${id}`;

	return (
		<TableRowContainer>
			<CellContainer id={id}>{label}</CellContainer>
			<CellContainer>
				{data.inProductChannel?.isEditable && (
					<NotificationsConfigCheckbox
						aria-labelledby={`${id} ${headCheckboxIds.IN_PRODUCT}`}
						id={inProductCheckboxId}
						name={inProductCheckboxId}
						testId={`personal-settings-notifications-config-table.ui.notifications-config-row.${inProductCheckboxId}`}
						isChecked={!!data.inProductChannel?.isEnabled}
						onChange={(ev) => onCheckboxChange(ev, IN_PRODUCT)}
					/>
				)}
			</CellContainer>
			<CellContainer>
				{data.emailChannel?.isEditable && (
					<NotificationsConfigCheckbox
						aria-labelledby={`${id} ${headCheckboxIds.EMAIL}`}
						id={emailCheckboxId}
						isDisabled={enabledData.options.isEmailNotificationEnabled === false}
						name={emailCheckboxId}
						testId={`personal-settings-notifications-config-table.ui.notifications-config-row.${emailCheckboxId}`}
						isChecked={!!data.emailChannel.isEnabled}
						onChange={(ev) => onCheckboxChange(ev, EMAIL)}
					/>
				)}
			</CellContainer>
		</TableRowContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableRowContainer = styled.tr({
	height: `${token('space.600')}`,

	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});
